import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Search } from 'lucide-react';
import { searchScheduledOfferings } from '../lib/airtable';
import { Spinner } from './ui/spinner';
import { useToast } from '@/hooks/use-toast';


const SOSearchDropdown = ({ 
  value, 
  onChange, 
  onSubmit, 
  loading: submitLoading,
  placeholder = "Enter SO number" 
}) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const justSelected = useRef(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const suggestionRefs = useRef([]);

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setIsDropdownOpen(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchScheduledOfferings(query);
      setSuggestions(results);
      setIsDropdownOpen(results.length > 0);
      setSelectedIndex(-1); // Reset selection when new results arrive
    } catch (error) {
      console.error('Search error:', error);
      setSuggestions([]);
      setIsDropdownOpen(false);
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "Failed to search for SO numbers. Please try again."
      });
    } finally {
      setIsSearching(false);
    }
  }, [toast]);

  // Debounce search requests
  useEffect(() => {
    // Skip search if we just selected a suggestion
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 500);


    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchQuery, performSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sync with external value changes
  useEffect(() => {
    setSearchQuery(value);
    // Close dropdown when value is set externally (e.g., from URL)
    if (value) {
      setIsDropdownOpen(false);
      setSuggestions([]);
      setSelectedIndex(-1);
    }
  }, [value]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    justSelected.current = true; // Prevent auto-search from triggering
    setSearchQuery(suggestion.soNumber);
    onChange(suggestion.soNumber);
    setIsDropdownOpen(false);
    setSelectedIndex(-1);
    setSuggestions([]);
    
    // Focus back to input after selection
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Trigger the main search with the selected SO directly
    onSubmit(suggestion.soNumber);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isDropdownOpen || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else {
          onSubmit();
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
        setSuggestions([]);
        break;
      
      default:
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  return (
    <div className="relative w-full">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex-1 relative">
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="w-full h-11 text-base pr-8"
            autoComplete="off"
            autoFocus
            role="combobox"
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
            aria-label="Search for scheduled offerings"
          />
          
          {/* Search icon or loading spinner */}
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            {isSearching ? (
              <Spinner size="sm" />
            ) : (
              <Search className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
        
        <Button 
          onClick={onSubmit}
          disabled={submitLoading}
          className="sm:w-auto w-full inline-flex items-center justify-center min-h-[44px] h-11 px-6 text-base"
        >
          {submitLoading ? (
            <>
              <Spinner size="sm" className="inline mr-2" />
              <span>Searching...</span>
            </>
          ) : 'Search'}
        </Button>
      </div>

      {/* Dropdown suggestions */}
      {isDropdownOpen && suggestions.length > 0 && (
        <Card 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 z-50 max-h-80 overflow-y-auto border shadow-lg bg-background"
          role="listbox"
        >
          <CardContent className="p-0">
            {suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.soNumber}-${index}`}
                ref={el => suggestionRefs.current[index] = el}
                className={`
                  p-4 cursor-pointer border-b border-border last:border-b-0 
                  min-h-[44px] flex items-center
                  ${selectedIndex === index 
                    ? 'bg-accent text-accent-foreground' 
                    : 'hover:bg-accent/50'
                  }
                `}
                onClick={() => handleSuggestionSelect(suggestion)}
                role="option"
                aria-selected={selectedIndex === index}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                    <div className="font-medium text-foreground">
                      {suggestion.soNumber}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {suggestion.programName}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1">
                    {suggestion.dates && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{suggestion.dates}</span>
                      </div>
                    )}
                    
                    {suggestion.status && (
                      <span className={`
                        inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                        ${suggestion.status === 'Active' 
                          ? 'bg-green-100 text-green-800'
                          : suggestion.status === 'Completed'
                          ? 'bg-gray-100 text-gray-800'
                          : suggestion.status === 'Cancelled'
                          ? 'bg-red-100 text-red-800'
                          : suggestion.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                        }
                      `}>
                        {suggestion.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SOSearchDropdown;